import { difference, isEqual, isNil } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { moduleActions } from 'src/redux/modules';
import {
  isCustomerWebViewEnabled,
  isEmbeddedInIFrame,
  isSalesforceRep,
} from 'src/redux/modules/authentication/authentication.module.selectors';
import { getSelectedBank } from 'src/redux/modules/bankSelection/bankSelection.selectors';
import { VALIDATE_RECURRING_IRA_REDEMPTION_FULFILLED } from 'src/redux/modules/executeRecurringPurchase/executeRecurringPurchase.module';
import { getExecuteRecurringPurchase } from 'src/redux/modules/executeRecurringPurchase/executeRecurringPurchase.selectors';
import {
  getEligibleBanks,
  getIsSingleBankWithDisabledStatus,
} from 'src/redux/modules/existingBanks/existingBanks.selectors';
import { getIRALimitData } from 'src/redux/modules/funds/IRA/IRA.selectors';
import { getAgreementMap } from 'src/redux/modules/funds/UserFunds/userFunds.selectors';
import {
  AUTOMATIC_EDIT_NO_CHANGES_MADE,
  CANCEL_TRADE_CONFIRMATION_MODAL,
  IRA_RECURRING_CONTRIBUTION_LIMIT_MODAL,
} from 'src/redux/modules/modals/modals.module';
import { getModals } from 'src/redux/modules/modals/modals.selectors';
import { getProductTypes } from 'src/redux/modules/productTypes/productType.selectors';
import { getRecurringStartDate } from 'src/redux/modules/shoppingCart/actions';
import {
  getAvailableEditFund,
  getCartLookup,
  getCurrentOrFutureYearIRAContribution,
  getCurrentOrFutureYearIRAContributionEdit,
  getOriginalFund,
  getRecurringPurchasingFunds,
  hasStopTransferFunds,
} from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import { dispatchPageLoad } from 'src/redux/utils/analytics-helper';
import { ProductTypes } from 'src/settings/settings';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import { isIRA } from 'src/utils/IRA/IRA-utility';
import { getPaperless } from 'src/utils/submission/submission-utility';
import {
  isRecurringBuyEdit,
  isRecurringContribute,
} from 'src/utils/transactions-util';
import { extractValueFromLabelValuePairArray } from 'src/utils/utils';
import FundList from '../../Buy/BuyFlow/FundList';
import FundingSource from '../../FundingSource/FundingSource/FundingSource';
import ProgressStep from '../../ProgressStep/ProgressStep';
import AppTitle from '../../Shared/AppTitle/AppTitle';
import TRP from '../../Shared/TRP/TRP';
import RecurringReview from '../RecurringReview/RecurringReview';
import './RecurringBuySteps.scss';
import If from '../../Shared/If/If';
import { isAPP, isRAS } from '../../../utils/advice/advice-utility';

function isFundListNextButtonDisabled(purchasingFunds) {
  return (
    purchasingFunds.length === 0 ||
    purchasingFunds.some(
      (fund) =>
        fund.currentYearError ||
        fund.priorYearError ||
        fund.combinedError ||
        fund.combinedDailyMaxError ||
        fund.daySelectionError ||
        fund.monthSelectionError
    )
  );
}

export class RecurringBuySteps extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showIRAContributionLimitModal: false,
      stepTwoValidationLoading: false,
      validateIRAContribution: true,
      validateStartDate: true,
    };
    this.isOneOfSelectedTaxableFund =
      this.isOneOfSelectedTaxableFund.bind(this);
    this.onClickScroll = this.onClickScroll.bind(this);
    this.onStepTwoButtonClick = this.onStepTwoButtonClick.bind(this);
    this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
    this.resetStepTwoValidationFlags =
      this.resetStepTwoValidationFlags.bind(this);
    this.stepTwoValidationHandler = this.stepTwoValidationHandler.bind(this);
  }

  getEditFund(funds) {
    return isRecurringBuyEdit() && funds ? funds[0] : {};
  }

  getAutomaticEditBank(fund) {
    return fund.bank ? fund.bank[0] : null;
  }

  getAutomaticEditAgreementId(fund) {
    return fund ? fund.agreementID : null;
  }

  canSelectBank(selectedBank, automaticEditBank) {
    return !selectedBank && isRecurringBuyEdit() && automaticEditBank;
  }

  showIRAContributionModal(
    currStep,
    prevStep,
    selectedTaxableFund,
    contributionUpdateDetected,
    frequencyUpdateDetected
  ) {
    return (
      (currStep === 2 || prevStep === 3) &&
      !selectedTaxableFund &&
      (contributionUpdateDetected || frequencyUpdateDetected)
    );
  }

  componentDidUpdate(prevProps) {
    const {
      actions: {
        acknowledgeIRAContributionLimit,
        selectBank,
        selectFund,
        updateFundAmount,
      },
      availableFunds,
      currentOrFutureYearIRAContribution: {
        systematicTransactions,
        totalContribution,
      },
      currentStep,
      executePurchase: {
        hasAcknowledgedIRAContributionLimit,
        validation: { validationFailed, validationResultLoaded },
      },
      modals,
      modals: { modalsSeen },
      previousStep,
      purchasingFunds,
      selectedBank,
      cartLookup,
    } = this.props;

    const selectedTaxableFund = purchasingFunds.some(
      (fund) => fund.displayClassification === 'NON_RETIREMENT'
    );

    const contributionUpdateDetected =
      prevProps.currentOrFutureYearIRAContribution.totalContribution !==
      totalContribution;
    const frequencyUpdateDetected = !isEqual(
      prevProps.currentOrFutureYearIRAContribution.systematicTransactions,
      systematicTransactions
    );
    const isOpenIRAContributionLimitModal = !!(
      !isNil(modalsSeen) &&
      modalsSeen[IRA_RECURRING_CONTRIBUTION_LIMIT_MODAL] &&
      !isNil(modals[IRA_RECURRING_CONTRIBUTION_LIMIT_MODAL])
    );
    const isOpenIRAContributionLimitModalPrev = !!(
      !isNil(prevProps.modals.modalsSeen) &&
      prevProps.modals.modalsSeen[IRA_RECURRING_CONTRIBUTION_LIMIT_MODAL] &&
      !isNil(prevProps.modals[IRA_RECURRING_CONTRIBUTION_LIMIT_MODAL])
    );
    const editFund = this.getEditFund(availableFunds);
    const automaticEditBank = this.getAutomaticEditBank(editFund);
    const automaticEditAgreementID = this.getAutomaticEditAgreementId(editFund);
    if (this.canSelectBank(selectedBank, automaticEditBank)) {
      selectBank(automaticEditBank);
    }
    if (
      isRecurringBuyEdit() &&
      automaticEditAgreementID &&
      !cartLookup[automaticEditAgreementID]
    ) {
      selectFund(automaticEditAgreementID, 0);
      updateFundAmount(
        automaticEditAgreementID,
        editFund.currentYearAmount,
        'currentYearAmount',
        0,
        isIRA(editFund)
      );
    }

    if (
      this.showIRAContributionModal(
        currentStep,
        previousStep,
        selectedTaxableFund,
        contributionUpdateDetected,
        frequencyUpdateDetected
      )
    ) {
      this.setState({
        showIRAContributionLimitModal: true,
        validateIRAContribution:
          contributionUpdateDetected || frequencyUpdateDetected,
        validateStartDate: frequencyUpdateDetected,
      });

      if (hasAcknowledgedIRAContributionLimit) {
        acknowledgeIRAContributionLimit(false);
      }
    }

    if (
      currentStep === 2 &&
      !selectedTaxableFund &&
      validationResultLoaded &&
      !hasAcknowledgedIRAContributionLimit &&
      isOpenIRAContributionLimitModal !== isOpenIRAContributionLimitModalPrev &&
      (!contributionUpdateDetected || !frequencyUpdateDetected)
    ) {
      this.setState({
        showIRAContributionLimitModal: true,
      });
    }

    if (
      currentStep === 2 &&
      !selectedTaxableFund &&
      validationFailed &&
      !hasAcknowledgedIRAContributionLimit &&
      isOpenIRAContributionLimitModal !== isOpenIRAContributionLimitModalPrev &&
      (!contributionUpdateDetected || !frequencyUpdateDetected)
    ) {
      this.setState({
        showIRAContributionLimitModal: true,
        validateIRAContribution: true,
        validateStartDate: true,
      });
    }
  }

  isOneOfSelectedTaxableFund() {
    const { purchasingFunds } = this.props;
    return purchasingFunds.some(
      (fund) => fund.displayClassification === 'NON_RETIREMENT'
    );
  }

  onClickScroll(step, element) {
    const {
      actions: { setProgress },
    } = this.props;
    setProgress(step);
    scrollElementIntoFullView(element, 15);
  }

  getCheckMonths(availableEditFund) {
    return (
      !isAPP(availableEditFund[0].productType) &&
      !isRAS(availableEditFund[0].productType)
    );
  }
  getMonthsCheck(newMonths, originalFund) {
    return (
      difference(newMonths, originalFund.transferMonths).length === 0 &&
      difference(originalFund.transferMonths, newMonths).length === 0
    );
  }
  getDaysCheck(newDays, originalFund) {
    return (
      difference(newDays, originalFund.transferDays).length === 0 &&
      difference(originalFund.transferDays, newDays).length === 0
    );
  }
  checkIsRecurringAndFundAvailable(availableEditFund) {
    return isRecurringBuyEdit() && availableEditFund;
  }
  checkDaysAmountBank(daysCheck, amountCheck, bankCheck) {
    return daysCheck && amountCheck && bankCheck;
  }

  onStepTwoButtonClick() {
    const {
      showIRAContributionLimitModal,
      validateIRAContribution,
      validateStartDate,
    } = this.state;

    const {
      actions: { openModal },
      originalFund,
      availableEditFund,
      selectedBank,
    } = this.props;

    if (this.checkIsRecurringAndFundAvailable(availableEditFund)) {
      const newMonths = availableEditFund[0].transferMonths.map(
        (month) => month.value
      );
      const newDays = availableEditFund[0].transferDays.map((day) => day.value);
      const checkMonths = this.getCheckMonths(availableEditFund);

      const monthsCheck = this.getMonthsCheck(newMonths, originalFund);

      const daysCheck = this.getDaysCheck(newDays, originalFund);

      const amountCheck =
        availableEditFund[0].currentYearAmount ===
        originalFund.currentYearAmount;
      const bankCheck =
        selectedBank.bankAgreementID === originalFund.bank[0].bankAgreementID;

      if (checkMonths) {
        if (monthsCheck && daysCheck && amountCheck && bankCheck) {
          openModal(AUTOMATIC_EDIT_NO_CHANGES_MADE);
          return;
        }
      } else {
        if (this.checkDaysAmountBank(daysCheck, amountCheck, bankCheck)) {
          openModal(AUTOMATIC_EDIT_NO_CHANGES_MADE);
          return;
        }
      }
    }

    if (
      !showIRAContributionLimitModal &&
      !validateIRAContribution &&
      !validateStartDate
    ) {
      this.onClickScroll(3, 'schedule-frequency-step');
      return;
    }

    if (
      showIRAContributionLimitModal &&
      !validateIRAContribution &&
      !validateStartDate
    ) {
      this.openIRAContributionLimitModal(true);
      this.resetStepTwoValidationFlags();
      return;
    }
    this.stepTwoValidationHandler();
  }

  onSubmitButtonClick() {
    const {
      actions: { submitRecurringPurchase, updateSystematicTransaction },
      dispatch,
      paperlessSetting,
      purchasingFunds,
      selectedBank,
    } = this.props;
    const { paperless, updatedPaperless } = paperlessSetting;
    const oldPaperless = getPaperless(paperless, ProductTypes.MUTUALFUND);
    if (isRecurringBuyEdit()) {
      dispatch(
        updateSystematicTransaction(
          purchasingFunds[0],
          purchasingFunds[0].deferMonths,
          'MODIFY',
          'PURCHASE',
          selectedBank
        )
      );
    } else {
      submitRecurringPurchase(
        updatedPaperless,
        oldPaperless,
        purchasingFunds,
        selectedBank
      );
    }
    scrollElementIntoFullView('buy-page-container');
  }

  openIRAContributionLimitModal(isValidated) {
    const {
      actions: { openModal },
      IRALimitData,
    } = this.props;
    openModal(IRA_RECURRING_CONTRIBUTION_LIMIT_MODAL, {
      IRALimitData,
      isValidated,
      buttonActionPostHandler: () =>
        this.onClickScroll(3, 'schedule-frequency-step'),
    });
  }

  getSystematicSelections() {
    const { purchasingFunds } = this.props;
    return purchasingFunds.map((fund) => {
      const { agreementID, transferDays, transferMonths } = fund;
      const selectedTransferDaysArray =
        extractValueFromLabelValuePairArray(transferDays);
      const selectedTransferMonthsArray =
        extractValueFromLabelValuePairArray(transferMonths);
      return {
        agreementID,
        days: selectedTransferDaysArray,
        months: selectedTransferMonthsArray,
      };
    });
  }

  resetStepTwoValidationFlags() {
    const {
      IRALimitData: { willExceedLimitAtEndOfYearWithNoChanges },
    } = this.props;

    let showIRAContributionLimitModal = false;
    if (willExceedLimitAtEndOfYearWithNoChanges) {
      showIRAContributionLimitModal = true;
    }

    this.setState({
      showIRAContributionLimitModal,
      stepTwoValidationLoading: false,
      validateIRAContribution: false,
      validateStartDate: false,
    });
  }

  getExcludeIRAValidation(willExceedLimitAtEndOfYearWithNoChanges) {
    return (
      this.isOneOfSelectedTaxableFund() ||
      willExceedLimitAtEndOfYearWithNoChanges
    );
  }
  handleResponse(res) {
    res.forEach((response) => {
      const {
        action: { type },
      } = response;
      if (type === VALIDATE_RECURRING_IRA_REDEMPTION_FULFILLED) {
        const {
          value: {
            body: { willExceedCurrentYear, willExceedNextYear },
          },
        } = response;

        if (!willExceedCurrentYear && !willExceedNextYear) {
          this.onClickScroll(3, 'schedule-frequency-step');
        }

        if (willExceedCurrentYear || willExceedNextYear) {
          this.openIRAContributionLimitModal(true);
        }
      }
    });
  }

  stepTwoValidationHandler() {
    const {
      actions: { validateRecurringRedemption },
      currentOrFutureYearIRAContribution: { systematicTransactions },
      dispatch,
      IRALimitData: { willExceedLimitAtEndOfYearWithNoChanges },
    } = this.props;

    this.setState({
      stepTwoValidationLoading: true,
    });
    const systematicsSelections = this.getSystematicSelections();
    const serviceCallArray = [
      dispatch(getRecurringStartDate({ systematicsSelections })()),
    ];

    const excludeIRAValidation = this.getExcludeIRAValidation(
      willExceedLimitAtEndOfYearWithNoChanges
    );

    if (!excludeIRAValidation) {
      serviceCallArray.push(
        dispatch(validateRecurringRedemption(systematicTransactions))
      );
    }
    Promise.all(serviceCallArray).then(
      (res) => {
        if (this.isOneOfSelectedTaxableFund()) {
          this.onClickScroll(3, 'schedule-frequency-step');
        } else if (willExceedLimitAtEndOfYearWithNoChanges) {
          this.openIRAContributionLimitModal(true);
        }

        // Handling progression to step 3 on promise resolution rather
        // than componentDidUpdate for validateRecurringRedemption call
        // due to undesired user experience from React's eventual state update
        this.handleResponse(res);

        this.resetStepTwoValidationFlags();
      },
      () => {
        this.resetStepTwoValidationFlags();

        if (this.isOneOfSelectedTaxableFund()) {
          this.onClickScroll(3, 'schedule-frequency-step');
        } else {
          this.openIRAContributionLimitModal();
        }
      }
    );
  }

  getOnly529(hasAK529, hasMD529, hasMutualFund, hasNAT529) {
    return (hasAK529 || hasMD529 || hasNAT529) && !hasMutualFund;
  }
  getMarqueeSubtitle(only529) {
    if (isRecurringBuyEdit()) {
      return '';
    } else {
      if (only529) {
        return (
          <span aria-label="atlp-landing-page-automatic-contributions">
            Set up and manage automatic contributions into your 529 account(s).
          </span>
        );
      } else {
        return (
          <span aria-label="atlp-landing-page-recurring-purchases">
            Set up recurring purchases among your <TRP />
            <br /> investments.
          </span>
        );
      }
    }
  }
  getMarqueeTitle(only529) {
    if (isRecurringBuyEdit()) {
      return only529
        ? 'Edit Your Automatic Monthly Contribution'
        : 'Edit Your Automatic Buy';
    } else {
      return only529 ? 'Automatic Monthly Contributions' : 'Automatic Buy';
    }
  }

  getTitleText() {
    return isRecurringContribute() ? 'contribution' : 'transaction';
  }

  render() {
    const {
      isIFrame,
      isSalesforceRep,
      actions,
      availableEditFund,
      agreementMap,
      currentStep,
      customerWebViewEnabled,
      displayTradeDate,
      editModeBank,
      eligibleBanks,
      existingBanks: { fetchingAccounts: banksLoading, error, isLoaded },
      flattenedPurchasingFunds,
      isSingleBankWithDisabledStatus,
      openModal,
      productTypes: { hasAK529, hasMD529, hasMutualFund, hasNAT529 },
      purchasingFunds,
      paperlessSetting,
      selectedBank,
      stopTransfer,
    } = this.props;
    const { stepTwoValidationLoading } = this.state;
    const fundingTargetStep = 'funding-target-step';
    const only529 = this.getOnly529(
      hasAK529,
      hasMD529,
      hasMutualFund,
      hasNAT529
    );
    const marqueeSubtitle = this.getMarqueeSubtitle(only529);
    const marqueeTitle = this.getMarqueeTitle(only529);
    return (
      <form className="animated fadeInUp" onSubmit={(e) => e.preventDefault()}>
        <If test={!(isIFrame || (!customerWebViewEnabled && isSalesforceRep))}>
          <AppTitle
            mboxSubtitle=""
            mboxTitle=""
            subtitle={marqueeSubtitle}
            title={marqueeTitle}
          />
        </If>

        {/* STEP 1 */}
        <ProgressStep
          expanded
          showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
          OnClickHeader={() => {
            this.onClickScroll(1, fundingTargetStep);
          }}
          active={currentStep === 1}
          className={fundingTargetStep}
          clickable={currentStep > 1}
          complete={currentStep > 1}
          nextButtonDisabled={
            isNil(selectedBank) || isSingleBankWithDisabledStatus
          }
          nextButtonLabel={
            <span>
              <b>NEXT:</b> Amounts & Dates
            </span>
          }
          step={1}
          title={
            <span>
              Choose<span className="hidden-xs hidden-sm"> your </span> funding
              source
            </span>
          }
          onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
          onNextClick={() => {
            this.onClickScroll(2, fundingTargetStep);
          }}
        >
          <FundingSource
            isSalesforceRep={isSalesforceRep}
            customerWebViewEnabled={customerWebViewEnabled}
            actions={actions}
            active={currentStep === 1}
            agreementMap={agreementMap}
            automaticEditFund={availableEditFund}
            completed={currentStep > 1}
            dispatchPageLoad={dispatchPageLoad}
            editModeBank={editModeBank}
            error={error}
            fundingSource={eligibleBanks}
            isLoaded={isLoaded}
            isLoading={banksLoading}
            openDrawer={() => {
              this.onClickScroll(1, fundingTargetStep);
            }}
            selectedBank={selectedBank}
            stopTransfer={stopTransfer}
          />
        </ProgressStep>

        {/* STEP 2 */}
        <ProgressStep
          expanded
          showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
          active={currentStep === 2}
          className="schedule-frequency-step"
          clickable={
            currentStep > 2 ||
            (currentStep !== 2 &&
              !isFundListNextButtonDisabled(purchasingFunds))
          }
          complete={currentStep > 2}
          nextButtonClassName="e2e-step2-verify-submit"
          nextButtonDisabled={
            isFundListNextButtonDisabled(purchasingFunds) ||
            stepTwoValidationLoading
          }
          nextButtonLabel={
            <span>
              <b>NEXT:</b> Review Your Plan
            </span>
          }
          nextButtonLoading={stepTwoValidationLoading}
          step={2}
          title={
            <span>
              Select your investment(s) and {this.getTitleText()} schedule
            </span>
          }
          onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
          OnClickHeader={() => {
            (currentStep > 2 ||
              (currentStep !== 2 &&
                !isFundListNextButtonDisabled(purchasingFunds))) &&
              this.onClickScroll(2, fundingTargetStep);
          }}
          onNextClick={this.onStepTwoButtonClick}
        >
          <FundList
            isRecurring
            clickable={
              currentStep > 2 ||
              (currentStep !== 2 &&
                !isFundListNextButtonDisabled(purchasingFunds))
            }
            displayTradeDate={displayTradeDate}
          />
        </ProgressStep>

        {/* STEP 3 */}
        <ProgressStep
          showCancelLink={!(!customerWebViewEnabled && isSalesforceRep)}
          active={currentStep === 3}
          className="review-step"
          clickable={currentStep > 3}
          expanded={currentStep === 3}
          nextButtonDisabled={customerWebViewEnabled}
          nextButtonLabel="SUBMIT"
          step={3}
          title={
            <span>Review & submit your automatic {this.getTitleText()}</span>
          }
          onCancelClick={() => openModal(CANCEL_TRADE_CONFIRMATION_MODAL)}
          OnClickHeader={() => {
            currentStep >= 3 && this.onClickScroll(3, 'review-step');
          }}
          onNextClick={this.onSubmitButtonClick}
        >
          <RecurringReview
            actions={actions}
            dispatchPageLoad={dispatchPageLoad}
            displayTradeDate={displayTradeDate}
            flattenedPurchasingFunds={flattenedPurchasingFunds}
            paperlessSetting={paperlessSetting}
            selectedBank={selectedBank}
          />
        </ProgressStep>
      </form>
    );
  }
}

const mapStateToProps = (state) => {
  const {
    editModeBank,
    existingBanks,
    progress: { currentStep, previousStep },
  } = state;
  return {
    agreementMap: getAgreementMap(state),
    availableEditFund: getAvailableEditFund(state),
    cartLookup: getCartLookup(state),
    currentStep,
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    editModeBank,
    eligibleBanks: getEligibleBanks(state),
    executePurchase: getExecuteRecurringPurchase(state),
    existingBanks,
    IRALimitData: getIRALimitData(state),
    isIFrame: isEmbeddedInIFrame(state),
    isSalesforceRep: isSalesforceRep(state),
    isSingleBankWithDisabledStatus: getIsSingleBankWithDisabledStatus(state),
    modals: getModals(state),
    originalFund: getOriginalFund(state),
    previousStep,
    productTypes: getProductTypes(state),
    purchasingFunds: isRecurringBuyEdit()
      ? getAvailableEditFund(state)
      : getRecurringPurchasingFunds(state),
    selectedBank: getSelectedBank(state),
    stopTransfer: hasStopTransferFunds(state),
    currentOrFutureYearIRAContribution: isRecurringBuyEdit()
      ? getCurrentOrFutureYearIRAContributionEdit(state)
      : getCurrentOrFutureYearIRAContribution(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(moduleActions, dispatch),
  dispatch,
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  RecurringBuySteps
);
